/*
 * header.js — Pinzelli
 *
 * Custom elements vanilla du header :
 *   <sticky-header> : mesure --header-height / --header-top-position /
 *                     --announcement-bars-before-header-heights et gère l'état sticky.
 *   <header-menu-drawer>   : tiroir générique (details/summary + .js-drawer) et tiroir de
 *                     navigation mobile à panneaux glissants (.menu-panel / button.menu-link[data-ref]).
 *   <search-modal>  : tiroir de recherche, hérite de <header-menu-drawer>, focus auto sur le champ.
 *   <mega-menu>     : ouverture / fermeture des dropdowns et mégamenus au survol et au clavier.
 *
 * Aucune dépendance externe. Chargé avec `defer`.
 */
(() => {
  'use strict';

  const FOCUSABLE_SELECTOR = [
    'summary',
    'a[href]',
    'button:enabled',
    '[tabindex]:not([tabindex^="-"])',
    'area[href]',
    'input:not([type=hidden]):enabled',
    'select:enabled',
    'textarea:enabled',
    'iframe',
  ].join(', ');

  const setProp = (name, value) => document.documentElement.style.setProperty(name, value);

  const define = (name, ctor) => {
    if (!window.customElements.get(name)) window.customElements.define(name, ctor);
  };

  const debounce = (fn, wait) => {
    let timer;
    return (...args) => {
      clearTimeout(timer);
      timer = setTimeout(() => fn(...args), wait);
    };
  };

  const escapeAttr = (value) =>
    window.CSS && typeof window.CSS.escape === 'function'
      ? window.CSS.escape(value)
      : String(value).replace(/["\\]/g, '\\$&');

  /* ===================================================== <sticky-header> === */

  class StickyHeader extends HTMLElement {
    connectedCallback() {
      this.headerEl = this.querySelector('.js-header') || this;
      this.lastScroll = 0;
      this.stickyEnabled = this.dataset.sticky !== 'false';
      this.hideOnScrollDown = this.dataset.hideOnScrollDown === 'true';

      this.measure = this.measure.bind(this);
      this.onScroll = this.onScroll.bind(this);
      this.debouncedMeasure = debounce(this.measure, 100);

      this.measure();
      this.onScroll();

      window.addEventListener('scroll', this.onScroll, { passive: true });
      window.addEventListener('resize', this.debouncedMeasure);
      window.addEventListener('shopify:section:load', this.debouncedMeasure);
      window.addEventListener('shopify:section:unload', this.debouncedMeasure);

      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(this.debouncedMeasure);
        this.resizeObserver.observe(this.headerEl);
      }
    }

    disconnectedCallback() {
      window.removeEventListener('scroll', this.onScroll);
      window.removeEventListener('resize', this.debouncedMeasure);
      window.removeEventListener('shopify:section:load', this.debouncedMeasure);
      window.removeEventListener('shopify:section:unload', this.debouncedMeasure);
      if (this.resizeObserver) this.resizeObserver.disconnect();
    }

    static announcementHeights() {
      let total = 0;
      let visible = 0;
      document.querySelectorAll('.js-announcement-bar').forEach((bar) => {
        const rect = bar.getBoundingClientRect();
        total += rect.height;
        visible += Math.max(0, Math.min(rect.height, rect.bottom));
      });
      return { total, visible };
    }

    measure() {
      const headerHeight = this.headerEl.getBoundingClientRect().height || 0;
      setProp('--header-height', `${Number(headerHeight.toFixed(2))}px`);

      const { total, visible } = StickyHeader.announcementHeights();
      setProp('--announcement-bars-before-header-heights', `${Number(total.toFixed(2))}px`);
      setProp('--announcement-bars-before-header-visible-heights', `${Number(visible.toFixed(2))}px`);
      setProp('--header-top-position', `${Number((headerHeight + total).toFixed(2))}px`);

      let groupHeight = 0;
      document.querySelectorAll('.shopify-section-group-header-group').forEach((section) => {
        groupHeight += section.offsetHeight;
      });
      setProp('--header-group-height', `${Math.max(0, groupHeight - 1)}px`);
    }

    onScroll() {
      const y = window.scrollY;
      setProp('--window-scroll-y-position', y);

      if (y === 0) {
        document.body.classList.add('is-at-top');
        document.body.classList.remove('is-scrolled');
      } else {
        document.body.classList.add('is-scrolled');
        document.body.classList.remove('is-at-top');
      }

      if (this.stickyEnabled) {
        this.headerEl.classList.toggle('header--scrolled', y > 2);
        if (this.hideOnScrollDown) {
          const goingDown = y > this.lastScroll && y > this.headerEl.offsetHeight;
          const menuOpen = this.headerEl.classList.contains('menu-open');
          this.headerEl.classList.toggle('header--hidden', goingDown && !menuOpen);
        }
      }
      this.lastScroll = y;

      const { visible } = StickyHeader.announcementHeights();
      setProp('--announcement-bars-before-header-visible-heights', `${Number(visible.toFixed(2))}px`);
    }
  }

  define('sticky-header', StickyHeader);

  /* ======================================================= <header-menu-drawer> === */

  class MenuDrawer extends HTMLElement {
    connectedCallback() {
      this.details = this.querySelector('details');
      this.summary = this.querySelector('summary');
      this.drawer = this.querySelector('.js-drawer');
      if (!this.details || !this.summary || !this.drawer) return;

      this.closeButtons = Array.from(this.querySelectorAll('.js-btn-close-drawer')).filter(
        (btn) => btn.closest('.js-drawer') === this.drawer
      );
      this.focusTarget =
        this.querySelector('.js-drawer-focus-element') || this.closeButtons[0] || this.drawer;
      this.toggleButtons = [this.summary, ...this.closeButtons];

      this.setInitialAccessibilityAttr();
      this.bindEvents();
      this.initPanels();
    }

    setInitialAccessibilityAttr() {
      this.summary.setAttribute('role', 'button');
      if (this.drawer.id) this.summary.setAttribute('aria-controls', this.drawer.id);
      this.summary.setAttribute('aria-expanded', this.details.hasAttribute('open') ? 'true' : 'false');
    }

    bindEvents() {
      this.toggleButtons.forEach((btn) => {
        btn.addEventListener('click', (event) => {
          event.preventDefault();
          this.toggle();
        });
      });

      this.addEventListener('keydown', (event) => {
        if (event.key !== 'Escape') return;
        if (!this.details.classList.contains('menu-opening')) return;
        if (this.details.querySelector('details[open]')) return;
        this.close();
        this.summary.focus({ preventScroll: true });
      });

      this.addEventListener('keydown', (event) => {
        if (event.key !== 'Tab') return;
        if (!this.details.classList.contains('menu-opening')) return;
        this.trapFocus(event);
      });
    }

    trapFocus(event) {
      const focusable = Array.from(this.details.querySelectorAll(FOCUSABLE_SELECTOR)).filter(
        (el) => el.offsetParent !== null || el.tagName === 'SUMMARY'
      );
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus({ preventScroll: true });
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus({ preventScroll: true });
      }
    }

    toggle() {
      if (this.details.hasAttribute('open')) this.close();
      else this.open();
    }

    // Alias attendu par assets/predictive-search.js (thème d'origine) :
    // `this.drawer.parentElement.parentElement.toggleDrawer()` sur Échap.
    toggleDrawer() {
      this.toggle();
    }

    open() {
      document.querySelectorAll('.drawer__container.menu-opening').forEach((container) => {
        if (container === this.details) return;
        const host = container.closest('header-menu-drawer, search-modal');
        if (host && typeof host.close === 'function') host.close();
      });

      this.details.setAttribute('open', '');
      void this.drawer.offsetWidth; // force reflow → la transition CSS démarre
      this.details.classList.add('menu-opening');

      this.toggleButtons.forEach((btn) => {
        btn.classList.add('menu-is-open');
        btn.setAttribute('aria-expanded', 'true');
      });

      const header = document.getElementById('header');
      if (header) header.classList.add('menu-open');

      document.body.classList.add('drawer--is-open');
      document.body.style.overflow = 'hidden';
      document.body.style.width = '100%';

      setTimeout(() => {
        if (this.focusTarget) this.focusTarget.focus({ preventScroll: true });
      }, 200);

      this.dispatchEvent(new CustomEvent('drawer:open', { bubbles: true }));
    }

    close() {
      this.details.classList.remove('menu-opening');

      this.toggleButtons.forEach((btn) => {
        btn.classList.remove('menu-is-open');
        btn.setAttribute('aria-expanded', 'false');
      });

      const header = document.getElementById('header');
      if (header) header.classList.remove('menu-open');

      if (!document.querySelector('.drawer__container.menu-opening')) {
        document.body.classList.remove('drawer--is-open');
        document.body.style.overflow = '';
        document.body.style.width = '';
      }

      setTimeout(() => {
        if (!this.details.classList.contains('menu-opening')) this.details.removeAttribute('open');
      }, 500);

      this.resetPanels();
      this.dispatchEvent(new CustomEvent('drawer:close', { bubbles: true }));
    }

    /* --- panneaux glissants du menu mobile --- */

    initPanels() {
      this.panels = Array.from(this.querySelectorAll('.menu-panel'));
      this.primaryPanel = this.querySelector('.primary-menu-panel');
      if (!this.panels.length) return;

      /* Ne PAS se servir de `data-init` comme marqueur : le snippet menu-drawer.liquid
         le pose déjà sur chaque bouton, exprès, pour que initMobileMenu() de global.js
         n'y branche pas ses propres écouteurs. S'en servir ici aussi faisait sortir la
         boucle à tous les coups — aucun clic n'était branché et les sous-menus mobiles
         ne s'ouvraient pas. Le bug restait invisible tant qu'aucune entrée de menu
         n'avait d'enfants : sans enfants, aucun `button.menu-link` n'est rendu. */
      this.querySelectorAll('button.menu-link').forEach((button) => {
        if (button.hasAttribute('data-panel-bound')) return;
        button.setAttribute('data-panel-bound', 'true');
        button.addEventListener('click', () => this.showPanel(button.dataset.ref));
      });
    }

    showPanel(ref) {
      const target = ref
        ? this.querySelector(`.menu-panel[data-menu="${escapeAttr(ref)}"]`)
        : null;

      if (!target) {
        if (this.primaryPanel) this.primaryPanel.style.opacity = '1';
        this.panels.forEach((panel) => {
          if (!panel.classList.contains('is-active')) return;
          panel.classList.remove('is-active');
          panel.classList.add('is-back', 'non-active');
          setTimeout(() => panel.classList.remove('non-active'), 300);
        });
        return;
      }

      const active = this.querySelector('.menu-panel.is-active');
      const isNested = Boolean(active && active.hasAttribute('data-menu-nested'));

      this.panels.forEach((panel) => {
        if (!panel.classList.contains('is-active')) return;
        if (panel.hasAttribute('data-menu-nested')) {
          panel.classList.add('was-active');
          panel.classList.remove('is-active');
        } else {
          setTimeout(() => {
            panel.style.opacity = '0';
          }, 300);
          setTimeout(() => panel.classList.remove('is-active'), 500);
        }
        panel.classList.remove('is-back');
      });

      if (this.primaryPanel) this.primaryPanel.style.opacity = '0';

      target.classList.add('is-active');
      target.style.opacity = '1';
      target.classList.remove('is-back');
      if (isNested) target.classList.add('is-instant');

      setTimeout(() => {
        this.panels.forEach((panel) => panel.classList.remove('was-active'));
        target.classList.remove('is-instant');
      }, 300);
    }

    resetPanels() {
      if (!this.panels || !this.panels.length) return;
      this.panels.forEach((panel) => {
        panel.classList.remove('is-active', 'was-active', 'non-active', 'is-back', 'is-instant');
        panel.style.opacity = '';
      });
      if (this.primaryPanel) this.primaryPanel.style.opacity = '1';
    }
  }

  define('header-menu-drawer', MenuDrawer);

  /* ====================================================== <search-modal> === */

  class SearchModal extends MenuDrawer {
    open() {
      super.open();
      const input = this.querySelector('input[type="search"]');
      if (input) setTimeout(() => input.focus({ preventScroll: true }), 220);
    }
  }

  define('search-modal', SearchModal);

  /* ========================================================= <mega-menu> === */

  class MegaMenu extends HTMLElement {
    connectedCallback() {
      this.header = document.getElementById('header');
      this.items = Array.from(this.querySelectorAll('.js-nav-item[data-menu-item]'));
      this.closeTimer = null;

      this.items.forEach((item) => {
        const dropdown = item.querySelector('.js-dropdown');
        if (!dropdown) return;
        const link = item.querySelector(':scope > .header__nav-link');

        (link || item).addEventListener('mouseenter', () => {
          this.cancelClose();
          this.closeAll();
          dropdown.classList.add('is-visible');
          this.syncState();
        });
        dropdown.addEventListener('mouseenter', () => this.cancelClose());
        item.addEventListener('mousemove', () => this.cancelClose());
        dropdown.addEventListener('mousemove', () => this.cancelClose());

        const leave = (event) => {
          const related = event.relatedTarget;
          if (related && related.nodeType === 1 && (item.contains(related) || dropdown.contains(related))) {
            return;
          }
          this.scheduleClose();
        };
        item.addEventListener('mouseleave', leave);
        dropdown.addEventListener('mouseleave', leave);

        item.addEventListener('focusin', () => {
          this.closeAll();
          dropdown.classList.add('is-visible');
          this.syncState();
        });
        item.addEventListener('focusout', (event) => {
          if (event.relatedTarget && item.contains(event.relatedTarget)) return;
          dropdown.classList.remove('is-visible');
          this.syncState();
        });

        const toggle = item.querySelector('.header__nav-dropdown-toggle');
        if (toggle) {
          toggle.addEventListener('click', (event) => {
            event.preventDefault();
            const wasOpen = dropdown.classList.contains('is-visible');
            this.closeAll();
            if (!wasOpen) dropdown.classList.add('is-visible');
            toggle.setAttribute('aria-expanded', wasOpen ? 'false' : 'true');
            this.syncState();
          });
        }
      });

      this.addEventListener('mouseleave', () => {
        this.scheduleClose();
      });

      this.escapeHandler = (event) => {
        if (event.key !== 'Escape') return;
        this.closeAll();
        this.syncState();
      };
      document.addEventListener('keydown', this.escapeHandler);
    }

    disconnectedCallback() {
      if (this.escapeHandler) document.removeEventListener('keydown', this.escapeHandler);
      this.cancelClose();
    }

    cancelClose() {
      if (!this.closeTimer) return;
      clearTimeout(this.closeTimer);
      this.closeTimer = null;
    }

    scheduleClose() {
      this.cancelClose();
      this.closeTimer = setTimeout(() => {
        this.closeTimer = null;
        this.closeAll();
        this.syncState();
      }, 250);
    }

    closeAll() {
      this.querySelectorAll('.js-dropdown.is-visible').forEach((dropdown) => {
        dropdown.classList.remove('is-visible');
      });
      this.querySelectorAll('.header__nav-dropdown-toggle[aria-expanded="true"]').forEach((toggle) => {
        toggle.setAttribute('aria-expanded', 'false');
      });
    }

    syncState() {
      if (!this.header) return;
      this.header.classList.toggle('is-megamenu-open', Boolean(this.querySelector('.js-dropdown.is-visible')));
    }
  }

  define('mega-menu', MegaMenu);

  /* ============================== compteur de panier (mise à jour asynchrone) */

  const updateCartCount = (count) => {
    document.querySelectorAll('[data-cart-count]').forEach((badge) => {
      badge.textContent = count > 0 ? count : '';
      badge.classList.toggle('hidden', count === 0);
    });
  };

  document.addEventListener('cart:updated', (event) => {
    const detail = event.detail || {};
    const count = detail.item_count != null ? detail.item_count : detail.count || 0;
    updateCartCount(Number(count) || 0);
  });

  if (window.PUB_SUB_EVENTS && typeof window.subscribe === 'function') {
    window.subscribe(window.PUB_SUB_EVENTS.cartUpdate, (payload) => {
      const cart = (payload && (payload.cartData || payload.cart)) || {};
      if (cart.item_count != null) updateCartCount(Number(cart.item_count) || 0);
    });
  }
})();
